import React, { useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTracker } from '@dpdgroupuk/react-event-tracker';

import RouteList from '../../../components/LeftMenu/components/RouteComponents/RouteList';
import DriverDetailsDialog from '../../../features/driverDetails';
import ModalControlled from '../../../components/ModalControlled';
import { wrapPromise } from '../../../utils/promise';
import useLocationQueryParams from '../../../hooks/useLocationQueryParams';
import { useRouteClicks } from '../../../hooks/useRouteClicks';
import AddNewRouteDialog from '../../../features/addNewRoute';
import { changeRouteStatus } from '../../../store/reducers/routes';
import InBuildWarningModal from '../../../components/modals/InBuildWarningModal';
import RevertRouteWarning from '../../../components/modals/RouteStatusWarning';
import { ROUTES } from '../../../constants/analytics';
import RouteStatus from '../../../enums/RouteStatus';
import noop from '../../../utils/noop';
import { manuallyLockChange } from '../../../store/reducers/route';
import { getSelectedRouteFilters } from '../../../store/selectors/ui/filterPanel';
import VoidRouteWarningModal from '../../../components/modals/VoidRouteWarningModal';
import { getRouteDetailFetchError } from '../../../store/selectors/route';
import ChoiceRouteWarning from '../../../components/modals/ChoiceRouteWarning';

const RouteListContainer = () => {
  const tracker = useTracker();
  const driverDialogRef = useRef();
  const inBuildWarningRef = useRef();
  const revertStatusWarningRef = useRef();
  const voidStatusWarningRef = useRef();
  const addRouteDialogRef = useRef();
  const routeDetailError = useSelector(getRouteDetailFetchError);

  const choiceRouteWarningRef = useRef();
  const { routeId, detailVisible, sectionId } = useLocationQueryParams({
    parseNumbers: true,
  });
  const filters = useSelector(getSelectedRouteFilters);

  const dispatch = useDispatch();
  const [, onDoubleClick] = useRouteClicks();
  const isRouteDetail = !!(routeId && detailVisible);

  useEffect(() => {
    tracker.logEvent(ROUTES.LOAD);
  }, [tracker]);

  useEffect(() => {
    if (isRouteDetail && routeDetailError) {
      onDoubleClick({ routeId });
    }
  }, [isRouteDetail, onDoubleClick, routeDetailError, routeId]);

  const showDriverModal = useCallback((route) => {
    wrapPromise(
      driverDialogRef.current.showModal({
        route,
      })
    );
  }, []);

  const handleRouteStatusChange = useCallback(
    (newRouteStatus, routeId, currentRouteStatus) => {
      const updateStatus = async () => {
        const data = await dispatch(
          changeRouteStatus({
            routeStatus: newRouteStatus,
            routeId,
            isRouteDetail,
          })
        );
        if (data && !data.success && data.choiceRoute) {
          choiceRouteWarningRef.current
            .showModal({ choiceRoute: data.choiceRoute })
            .then(noop, noop);
        }
      };
      switch (currentRouteStatus) {
        case RouteStatus.VOID:
          voidStatusWarningRef.current.showModal().then(noop, noop);
          break;
        case RouteStatus.IN_BUILD:
          inBuildWarningRef.current
            .showModal({ routeId })
            .then(updateStatus, noop);
          break;
        case RouteStatus.DOWNLOADED:
        case RouteStatus.LOADING:
        case RouteStatus.DEPARTED:
          revertStatusWarningRef.current.showModal().then(updateStatus, noop);
          break;
        default:
          updateStatus();
      }

      tracker.logEvent(ROUTES.SELECT_ROUTES_STATUS);
    },
    [tracker, isRouteDetail, dispatch]
  );

  const handleRouteAddClick = useCallback(() => {
    wrapPromise(addRouteDialogRef.current.showModal());

    tracker.logEvent(ROUTES.CLICK_NEW_ROUTE_ICON);
  }, [tracker]);

  const handleLockChange = useCallback(
    (route) => {
      dispatch(manuallyLockChange(route));

      tracker.logEvent(
        ROUTES[route.routeLockedBy ? 'CLICK_UNLOCK_ROUTE' : 'CLICK_LOCK_ROUTE']
      );
    },
    [tracker, dispatch]
  );

  const onOpenLockRoutePopover = useCallback(() => {
    tracker.logEvent(ROUTES.CLICK_ROUTE_OPTIONS_MENU);
  }, [tracker]);

  return (
    <>
      <RouteList
        onRouteCodeClick={showDriverModal}
        routeId={routeId}
        sectionId={sectionId}
        filters={filters}
        isRouteDetail={isRouteDetail}
        onStatusChange={handleRouteStatusChange}
        onListAddItemClick={handleRouteAddClick}
        onLockChange={handleLockChange}
        onOpenLockRoutePopover={onOpenLockRoutePopover}
      />
      <ModalControlled
        Component={DriverDetailsDialog}
        ref={driverDialogRef}
        renderWhenOpen
      />
      <ModalControlled
        ref={inBuildWarningRef}
        renderWhenOpen
        Component={InBuildWarningModal}
      />
      <ModalControlled
        ref={choiceRouteWarningRef}
        renderWhenOpen
        Component={ChoiceRouteWarning}
      />
      <ModalControlled
        ref={revertStatusWarningRef}
        renderWhenOpen
        Component={RevertRouteWarning}
      />
      <ModalControlled
        ref={voidStatusWarningRef}
        renderWhenOpen
        Component={VoidRouteWarningModal}
      />
      <ModalControlled
        Component={AddNewRouteDialog}
        ref={addRouteDialogRef}
        renderWhenOpen
      />
    </>
  );
};

export default RouteListContainer;
